import { useState } from 'react';
import {
  Flex,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  Text,
} from '@chakra-ui/react';
import { Formik, FormikProps } from 'formik';
import { CloseIcon } from 'components/Icons';
import {
  applicationQuestionDefaults,
  ApplicationQuestionsFormValidation,
} from 'utils';
import { useUserContext } from 'context';
import Skills from './skills';
import OkayWith from './okayWith';
import Languages from './languages';
import LargestWeight from './largestWeight';
import EducationLevel from './educationLevel';
import Completed from './completed';

const ApplicationQuestions = ({
  open,
  onClose,
  assignment,
}: {
  open: boolean;
  onClose: () => void;
  assignment: AgencyAssignment | null;
}): JSX.Element => {
  const steps = 5;
  const [step, setStep] = useState(1);
  const { updateAgencyAssignment } = useUserContext();

  const next = () => {
    setStep(step + 1);
  };

  const prev = () => {
    setStep(step - 1);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <Skills next={next} />;
      case 2:
        return <OkayWith prev={prev} next={next} />;
      case 3:
        return <Languages prev={prev} next={next} />;
      case 4:
        return <LargestWeight prev={prev} next={next} />;
      case 5:
        return <EducationLevel prev={prev} />;
      default:
        return <Completed exit={onClose} />;
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        bg="custom.levoOffWhite"
        height="90%"
        mb={0}
        mt="auto"
        borderRadius="24px 24px 0 0"
      >
        <Formik
          validationSchema={ApplicationQuestionsFormValidation}
          initialValues={{
            ...applicationQuestionDefaults,
          }}
          onSubmit={async (applicationQuestions, { setSubmitting }) => {
            try {
              setSubmitting(true);
              if (updateAgencyAssignment && assignment?.id) {
                await updateAgencyAssignment({
                  id: assignment?.id || '',
                  applicationQuestions,
                });
              }
              next();
            } catch (error) {
              console.error(error);
            } finally {
              setSubmitting(false);
            }
          }}
          enableReinitialize
        >
          {({ isSubmitting }: FormikProps<ApplicationQuestionsValues>) => {
            return (
              <Flex direction="column" width="100%">
                <Flex
                  direction="row"
                  justifyContent="space-between"
                  p="24px 8px 24px 12px"
                >
                  <IconButton
                    variant="ghost"
                    aria-label="Close share profile modal"
                    icon={<CloseIcon boxSize="18px" />}
                    onClick={onClose}
                    disabled={isSubmitting}
                  />
                  <Flex
                    position="absolute"
                    left={0}
                    right={0}
                    direction="column"
                    zIndex={-1}
                  >
                    <Text textStyle="heading3" fontWeight="bold" align="center">
                      Application Questions
                    </Text>
                    <Text textStyle="small" mt={2} align="center">
                      {`${step} of ${steps} questions`}
                    </Text>
                  </Flex>
                </Flex>
                <Flex flexGrow={1} m={6}>
                  {renderStep()}
                </Flex>
              </Flex>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default ApplicationQuestions;
